import { useCallback, useEffect, useMemo, useState } from 'react';
import ChannelsIcon from '../../assets/icons/channels.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import { Button } from '../../components/Button';
import { ChannelSidePanel } from '../../components/channels';
import type { ChannelTestResultEntry } from '../../components/channels';
import { Toast, ToastProvider } from '../../components/Toast';
import { ResponsiveDataView } from '../../components/data-display/ResponsiveDataView';
import { AddChannelDialog } from '../../components/dialogs/AddChannelDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { ManageColumnsDialog } from '../../components/dialogs/ManageColumnsDialog';
import { ActiveFilterTags } from '../../components/filters/ActiveFilterTags';
import { useChannelFilterTags } from '../../components/filters/useChannelFilterTags';
import {
  NotificationChannel,
  useDeleteNotificationChannelMutation,
  useGetNotificationChannelQuery,
  useGetNotificationChannelTestResultsQuery,
  useSendTestNotificationMutation,
} from '../../gql/generated';
import { useChannelFilters } from '../../hooks/useChannelFilters';
import { ChannelListItemMobile } from './ChannelListItemMobile';
import { createChannelColumns } from './createChannelColumns';
import { useChannelColumnFilters } from './useChannelColumnFilters';
import { useChannelsTable } from './useChannelsTable';
import { logger } from '../../utils/logger';

export function ChannelsPage() {
  const state = useChannelsTable();
  const { hasActiveFilters, clearFilters, selectedId, setSelectedId } =
    useChannelFilters();
  const filterTags = useChannelFilterTags();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] =
    useState<NotificationChannel | null>(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState<Set<string>>(
    new Set()
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    title: '',
    description: '',
  });
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>(
    'success'
  );

  const [deleteChannel] = useDeleteNotificationChannelMutation();
  const [sendTestNotification] = useSendTestNotificationMutation();

  const selectedChannelFromTable = useMemo(
    () => state.channels.find((channel) => channel.id === selectedId) ?? null,
    [selectedId, state.channels]
  );

  const { data: selectedChannelData } = useGetNotificationChannelQuery({
    variables: { id: selectedId || '' },
    skip: !selectedId || !!selectedChannelFromTable,
  });

  const selectedChannel =
    selectedChannelFromTable || selectedChannelData?.notificationChannel || null;

  const {
    data: selectedChannelTestResultsData,
    refetch: refetchSelectedChannelTestResults,
  } = useGetNotificationChannelTestResultsQuery({
    variables: { id: selectedId || '', limit: 20 },
    skip: !selectedId,
    fetchPolicy: 'network-only',
  });

  const sidePanelOpen = selectedId !== null;

  const selectedChannelTestResults = useMemo(
    () =>
      (selectedChannelTestResultsData?.notificationChannelTestResults || []).map(
        (result): ChannelTestResultEntry => ({
          id: result.id,
          channelId: result.channelId,
          status: result.success ? 'success' : 'error',
          message: result.message,
          timestamp: result.createdAt,
        })
      ),
    [selectedChannelTestResultsData]
  );

  useEffect(() => {
    if (
      selectedId &&
      !selectedChannelFromTable &&
      selectedChannelData?.notificationChannel === null
    ) {
      setSelectedId(null);
    }
  }, [selectedChannelData, selectedChannelFromTable, selectedId, setSelectedId]);

  const handleRowClick = useCallback(
    (channel: NotificationChannel) => {
      setSelectedId(channel.id);
    },
    [setSelectedId]
  );

  const handleSidePanelClose = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedId(null);
      }
    },
    [setSelectedId]
  );

  // Get the column filter render function (reads filters directly from URL)
  const renderColumnFilter = useChannelColumnFilters();

  const handleDeleteClick = (channelId: string) => {
    setChannelToDelete(channelId);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (channel: NotificationChannel) => {
    setEditingChannel(channel);
    setIsAddDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsAddDialogOpen(open);
    if (!open) {
      // Clear editing channel when dialog closes
      setEditingChannel(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!channelToDelete) return;

    setIsDeleting(true);
    try {
      await deleteChannel({
        variables: { id: channelToDelete },
        refetchQueries: ['GetNotificationChannels'],
      });
    } catch (error) {
      logger.error('Failed to delete channel:', error);
    } finally {
      setIsDeleting(false);
      setChannelToDelete(null);
    }
  };

  const handleTestNotification = async (channelId: string) => {
    setLoadingChannels((prev) => new Set(prev).add(channelId));
    try {
      await sendTestNotification({
        variables: { id: channelId },
      });

      if (selectedId === channelId) {
        await refetchSelectedChannelTestResults();
      }

      setToastMessage({
        title: '',
        description: '✓ Test notification sent successfully',
      });
      setToastVariant('success');
      setToastOpen(true);
    } catch (error) {
      logger.error('Failed to send test notification:', error);

      if (selectedId === channelId) {
        await refetchSelectedChannelTestResults();
      }

      setToastMessage({
        title: '',
        description: '✕ Failed to send test notification',
      });
      setToastVariant('error');
      setToastOpen(true);
    } finally {
      setLoadingChannels((prev) => {
        const next = new Set(prev);
        next.delete(channelId);
        return next;
      });
    }
  };

  const columns = createChannelColumns({
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onTestNotification: handleTestNotification,
    loadingChannels,
    onColumnsClick: () => setColumnsDialogOpen(true),
  });

  return (
    <ToastProvider>
      <ChannelSidePanel
        open={sidePanelOpen}
        onOpenChange={handleSidePanelClose}
        channel={selectedChannel}
        availableChannels={state.channels}
        testResults={selectedChannelTestResults}
        onSendTestNotification={handleTestNotification}
        isSendingTestNotification={
          !!selectedChannel && loadingChannels.has(selectedChannel.id)
        }
      />

      <Toast
        open={toastOpen}
        onOpenChange={setToastOpen}
        title={toastMessage.title}
        description={toastMessage.description}
        variant={toastVariant}
      />
      {/* Manage Columns Dialog */}
      <ManageColumnsDialog
        open={columnsDialogOpen}
        onOpenChange={setColumnsDialogOpen}
        columns={columns}
        columnVisibility={state.columnVisibility}
        columnOrder={state.columnOrder}
        onColumnsChange={(visibleColumnIds, newColumnOrder) => {
          // Convert visible column IDs to visibility state
          const visibilityState: Record<string, boolean> = {};
          columns.forEach((col) => {
            const colId =
              'accessorKey' in col
                ? String(col.accessorKey)
                : col.id || 'unknown';
            // Always keep actions visible (pinned column)
            if (colId === 'actions') {
              visibilityState[colId] = true;
            } else {
              // Hidden columns are explicitly set to false
              visibilityState[colId] = visibleColumnIds.includes(colId);
            }
          });
          state.setColumnVisibility(visibilityState);

          // Set the new column order
          state.setColumnOrder(newColumnOrder);
        }}
      />

      {/* Add/Edit Channel Dialog */}
      <AddChannelDialog
        open={isAddDialogOpen}
        onOpenChange={handleDialogClose}
        editingChannel={editingChannel}
        onSuccess={() => {
          // Apollo will refetch automatically with refetchQueries in the mutation
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title='Delete Channel'
        description='Are you sure you want to delete this channel? This action cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        isNegative
        loading={isDeleting}
      />

      {/* Main Content */}
      <ResponsiveDataView
        data={state.channels}
        columns={columns}
        isLoading={state.loading}
        onRefresh={async () => {
          await state.refetch();
        }}
        error={state.error}
        getRowId={(channel) => channel.id}
        emptyStateMessage='No channels found'
        pageIndex={state.pageIndex}
        pageSize={state.pageSize}
        onPageChange={state.setPageIndex}
        onPageSizeChange={state.setPageSize}
        sorting={state.sorting}
        onSortingChange={state.setSorting}
        columnPinning={state.columnPinning}
        onColumnPinningChange={state.setColumnPinning}
        columnVisibility={state.columnVisibility}
        onColumnVisibilityChange={state.setColumnVisibility}
        columnOrder={state.columnOrder}
        onColumnOrderChange={state.setColumnOrder}
        columnSizing={state.columnSizing}
        onColumnSizingChange={state.onColumnSizingChange}
        resetColumnSizing={state.resetColumnSizing}
        totalPages={state.totalPages}
        totalCount={state.totalCount}
        renderListItem={(channel, config) => (
          <ChannelListItemMobile channel={channel} config={config} />
        )}
        onRowClick={handleRowClick}
        columnFilters={renderColumnFilter}
        activeFilters={
          hasActiveFilters ? (
            <ActiveFilterTags tags={filterTags} onClearAll={clearFilters} />
          ) : undefined
        }
        toolbarActions={
          <Button
            variant='primary'
            onClick={() => setIsAddDialogOpen(true)}
            leadingIcon={<PlusIcon className='h-4 w-4' />}
          >
            Add Channel
          </Button>
        }
        enableManageColumns
        ariaLabel='Channels'
        title='Channels'
        icon={<ChannelsIcon />}
      />

    </ToastProvider>
  );
}
